<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row mb-3">
      <div class="col-sm-4">
        <h4 qid="threats-page-title" class="mb-2">
          {{ $t('maintenance.threats.title') }}
        </h4>
        <div>
          <span v-if="getGuide('threats_list')">
            <GuideButton :guide="getGuide('threats_list')"></GuideButton>
          </span>
        </div>
      </div>
      <div class="col-sm-8 text-right">
        <button
            class="btn btn-secondary-dark float-right"
            @click="filterOpened = !filterOpened"
            qid="threats-create-button"
        >
          <font-awesome-icon icon="filter"/>
        </button>
        <router-link
            to="/maintenance/threats/create"
            class="btn btn-success float-right"
            qid="create-t-button"
            v-if="isGranted(permissions.RETENTION_RULE_CREATE)"
        >
          <font-awesome-icon icon="plus-circle"/> {{ $t('maintenance.threats.create_button') }}
        </router-link>
        <b-form-input
          type="text"
          v-model="filters.keywordValue"
          class="float-right mw-300 mr-3 search-bar"
          qid="search-t-input"
          v-on:input="debounceInput"
          :placeholder="$t('maintenance.threats.search_placeholder')"
        ></b-form-input>
      </div>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="card">
          <table class="table table-responsive-md">
            <thead>
              <tr>
                <th scope="col"
                    class="cursor-pointer"
                    @click="selectSort('name')"
                    qid="sort-t-name"
                >
                  {{ $t('maintenance.threats.table.name') }}
                  <span class="ml-2">
                    <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                    <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                    <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                  </span>
                </th>
                <th scope="col">
                  {{ $t('maintenance.threats.table.actors') }}
                </th>
                <th width="130"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                  <router-link
                      :to="{ name: 'maintenance.threats.view', params: { slug: object.slug }}"
                      qid="t-view-page-link"
                  >
                    {{ object.name }}
                  </router-link>
                </td>
                <td>
                  <span class="mr-1" v-for="actor in object.actors" qid="p-tags-list">
                    <span class="badge badge-info mb-1">
                      {{actor.name}}
                    </span>
                  </span>
                </td>
                <td>
                  <span v-if="isGranted([permissions.RETENTION_RULE_EDIT, permissions.RETENTION_RULE_EDIT_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-edit"
                        qid="edit-t-modal"
                        @click="showEditRRModal(object)"
                  >
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span v-if="isGranted([permissions.RETENTION_RULE_DELETE, permissions.RETENTION_RULE_DELETE_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-delete"
                        qid="delete-t-modal"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    label="value"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group>
              <label qid="create-do-tags-label">
                {{ $t('maintenance.threats.create.actor_label') }}
              </label>
              <multiselect
                  v-model="filters.actors"
                  @select="debounceInput"
                  @remove="debounceInput"
                  :options="actors"
                  :multiple="true"
                  :taggable="false"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="name"
                  :placeholder="$t('maintenance.threats.create.actor_label')"
                  label="name"
                  qid="filter-p-form-tags"
                  @search-change="loadActors"
                  @tag="addActor"
              ></multiselect>
            </b-form-group>
            <a @click="resetFilters" class="text-right" qid="filter-t-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete retention rule confirm modal template -->
    <b-modal ref="delete-retention-rule" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-t-title">
          {{ $t('maintenance.threats.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.threats.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.threats.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-t-cancel-button"
        >
          {{ $t('maintenance.threats.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteRR(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-t-submit-button"
                  v-if="isGranted([permissions.RETENTION_RULE_DELETE, permissions.RETENTION_RULE_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.threats.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit retention rule modal template -->
    <b-modal ref="edit-retention-rule" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-t-title">
          {{ $t('maintenance.threats.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-t-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editRRFormSubmit" qid="edit-t-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-t-name-label">
              {{ $t('maintenance.threats.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-t-form-name"
              type="text"
              v-model="tForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.threats.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-t-description-label">
              {{ $t('maintenance.threats.create.description_label') }}
            </label>
            <b-form-textarea
              qid="edit-t-form-description"
              v-model="tForm.description"
              rows="6"
              max-rows="6"
              required
              :placeholder="$t('maintenance.threats.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <label qid="create-s-tags-label">
              {{ $t('maintenance.threats.create.actor_label') }}
            </label>
            <multiselect
                v-model="selectedActors"
                :options="actors"
                :multiple="true"
                :taggable="true"
                track-by="name"
                :tagPlaceholder="$t('maintenance.threats.create.actor_dropdown_label')"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('maintenance.threats.create.actor_placeholder')"
                label="name"
                qid="create-do-tags-select"
                @search-change="loadActors"
                @tag="addActor"
            ></multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditRRModal"
                    qid="edit-t-cancel-button"
          >
            {{ $t('maintenance.threats.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-t-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.threats.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'RetentionRules',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.threats.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        tForm: {
          name: '',
          description: '',
          actors: [],
          client:'',
        },
        error: '',
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          actors: [],
          keywordValue: ''
        },
        timeLimitItems: [],
        timeLimitInput: '',
        loading: true,
        buttonDisabled: false,
        myGuides: [],
        actors: [],
        selectedActors: [],
        newActors: []
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
        this.loadActors();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Maintenance',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      addActor(newActor) {
        const actor = {
          name: newActor,
          translatable: newActor,
          name_translated: newActor
        }
        this.selectedActors.push(actor)
        this.newActors.push(actor)
      },
      resetFilters() {
        this.filters.actors = [],
        this.debounceInput()
      },
      handleTimeLimitInput(input) {
        this.timeLimitInput = input;
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;
        let formActors = []

        if (this.filters.actors && this.filters.actors.length) {
          for (var t = 0; t < this.filters.actors.length; t++) {
            formActors[t] = this.filters.actors[t].id;
          }
          this.filtersOpened = true
        }

        if (this.filters.keywordValue !== '') {
          this.filtersOpened = true
        }

        piincHttp.get('threats', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            actors: formActors ? formActors : undefined,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-retention-rule'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-retention-rule'].hide()
      },
      showEditRRModal(object) {
        this.error = ''
        let self = this;

        if (object.actors && object.actors.length) {
          this.selectedActors = _.cloneDeep(object.actors);
        }

        this.tForm.slug = object.slug;
        this.tForm.name = object.name;
        this.tForm.description = object.description;
        this.$refs['edit-retention-rule'].show()
      },
      hideEditRRModal() {
        this.selectedActors = []
        this.$refs['edit-retention-rule'].hide()
      },
      editRRFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        this.tForm.client = this.getClient.slug;

        this.tForm.actors = [];
        if (this.selectedActors && this.selectedActors.length) {
          for (var i = 0; i < this.selectedActors.length; i++) {
            this.tForm.actors.push(this.selectedActors[i].name);
          }
        }

        if (this.selectedActors && this.selectedActors.length) {
          piincHttp.put('threats/' + this.tForm.slug, this.tForm).then(function(response) {
            self.hideEditRRModal();
            self.buttonDisabled = false;
            self.loadContent(self.currentPage);
            self.$toastr('success', self.$t('maintenance.threats.update.success'))
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
        } else {
          this.error = this.$t('maintenance.threats.create.actors_required')
          this.buttonDisabled = false;
        }
      },
      confirmDeleteRR(object) {
        let self = this;
        piincHttp.delete('threats/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.threats.delete.success'))
        }, function() {});
      },
      loadActors(query) {
        let self = this;
        piincHttp.get('actors', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.actors = response.data.items
          for (var i = 0; i < self.newActors.length; i++) {
            self.actors.push(self.newActors[i]);
          }
          if (self.actors && !self.actors.length && !query) {
            self.actors.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      }
    },
    components: {
      GuideButton
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'threats',
        filters: this.filters
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.threats) {
          this.filters = this.getFilters.threats
        }
        this.loadContent();
        this.loadActors();
      }
      this.loadGuides('threats_list');
    }
  }
</script>
